import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Grid, theme, Spin, Modal } from "antd";
import { ArrowLeftOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { useAlbumMedia } from "./useAlbumMedia";
import { useAuth } from "../context/AuthContext";
import { getAlbumMedia } from "./api";
import { loadLeaflet } from "./leaflet";
import DualcamPlayerModal from "./DualcamPlayerModal";
import { MediaThumbTile } from "./MediaThumbTile";
import type { MediaEntry } from "./types";
import { AlbumNoAccess } from "./AlbumNoAccess";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusTag } from "@dowty/ui";

const { useBreakpoint } = Grid;

type LocatedEntry = MediaEntry & { latitude: number; longitude: number };

// Screen-pixel radius (not raw lat/lng distance) within which pins get merged into one cluster
// marker — mirrors the Android app's clusterRadiusPx (56dp / 2), scaled for the smaller web icon.
const CLUSTER_RADIUS_PX = 30;
const ICON_SIZE_PX = 44;

const isNearAnyPoint = (point: any, groupPoints: any[], radiusPx: number) =>
    groupPoints.some(groupPoint => Math.hypot(groupPoint.x - point.x, groupPoint.y - point.y) < radiusPx);

const addNearbyPoints = (items: LocatedEntry[], points: any[], visited: boolean[], group: LocatedEntry[], groupPoints: any[], radiusPx: number) => {
    let changed = false;
    for (let j = 0; j < items.length; j++) {
        if (visited[j]) continue;
        const point = points[j];
        if (!isNearAnyPoint(point, groupPoints, radiusPx)) continue;
        visited[j] = true;
        group.push(items[j]);
        groupPoints.push(point);
        changed = true;
    }
    return changed;
};

/** Groups entries by proximity of their *projected screen position*, not lat/lng — same
 * single-pass nested-loop approach as MapActivity.rebuildClusters() on the Android side. Cluster
 * counts here are an album's worth of pins, not thousands, so this is plenty fast. */
function clusterByScreenProximity(L: any, map: any, items: LocatedEntry[], radiusPx: number): LocatedEntry[][] {
    const points = items.map(e => map.latLngToContainerPoint([e.latitude, e.longitude]));
    const visited = new Array(items.length).fill(false);
    const clusters: LocatedEntry[][] = [];

    for (let i = 0; i < items.length; i++) {
        if (visited[i]) continue;
        visited[i] = true;
        const group = [items[i]];
        const groupPoints = [points[i]];
        let changed = true;
        while (changed) changed = addNearbyPoints(items, points, visited, group, groupPoints, radiusPx);
        clusters.push(group);
    }
    return clusters;
}

/** Rounded-square thumbnail marker with a count badge top-right when the cluster has >1 item —
 * same visual intent as Android's buildIcon(), done here as an inline-styled divIcon instead of a
 * drawn Bitmap. className is overridden (rather than left default) so Leaflet's default
 * `.leaflet-div-icon` white-box-with-border styling doesn't show through behind our own markup. */
function buildClusterIcon(L: any, representative: LocatedEntry, count: number): any {
    const cornerRadius = ICON_SIZE_PX * 0.18;
    const thumbnailUrl = representative.thumbnailUrl.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
    const label = `${count} ${count === 1 ? "item" : "items"}`;
    const badgeCount = count > 99 ? "99+" : count;
    const badge = count > 1
        ? `<div style="position:absolute;top:-4px;right:-4px;min-width:18px;height:18px;border-radius:9px;
            background:var(--ui-info);border:2px solid var(--ui-surface);color:var(--ui-on-primary);font-size:12px;font-weight:bold;
            display:flex;align-items:center;justify-content:center;padding:0 3px;line-height:1;">
            ${badgeCount}</div>`
        : "";
    const html = `
        <div style="width:${ICON_SIZE_PX}px;height:${ICON_SIZE_PX}px;border-radius:${cornerRadius}px;
            background:var(--ui-surface);border:1px solid var(--ui-border-strong);position:relative;overflow:visible;">
            <img src="${thumbnailUrl}" alt="${label}" style="width:100%;height:100%;object-fit:cover;
                border-radius:${cornerRadius}px;display:block;" />
            ${badge}
        </div>`;
    return L.divIcon({
        html,
        className: "dualcam-cluster-icon",
        iconSize: [ICON_SIZE_PX, ICON_SIZE_PX],
        iconAnchor: [ICON_SIZE_PX / 2, ICON_SIZE_PX / 2],
    });
}

const DualcamMapPage: React.FC = () => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const { albumId } = useParams<{ albumId: string }>();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token: designToken } = theme.useToken();
    // includeGps: this screen is the only one that plots coordinates, so it accepts the extra backend
    // work the album grid deliberately skips. On a big album this list can take a while (the backend
    // backfills each not-yet-cached video's GPS from the NAS) — see previewMedia below for what fills
    // the map in the meantime.
    const { media, loading, notFound, loadError, reload } = useAlbumMedia(albumId, true);
    const { user } = useAuth();

    // Fast preview so the map isn't blank while the slow includeGps=true load above resolves.
    // Synology indexes GPS for photos already (never for videos — see the backend's
    // BackfillVideoGpsAsync doc), so a plain includeGps=false fetch returns those pins immediately.
    // One-shot, not a poll: the slow load above always returns the maximally-complete answer in a
    // single response (the backend blocks until every video's backfill attempt finishes), so a
    // second includeGps=false fetch would only re-read the same photo-only pins — nothing to gain
    // from asking again. Errors are swallowed: the slow load's own notFound/error handling
    // already covers real failures for this albumId, and this is a pure bonus, not required data.
    const [previewMedia, setPreviewMedia] = useState<MediaEntry[] | null>(null);
    useEffect(() => {
        setPreviewMedia(null);
        if (!user || !albumId) return;
        let cancelled = false;
        getAlbumMedia(user.token, Number(albumId), false)
            .then(result => { if (!cancelled) setPreviewMedia(result.items); })
            .catch(() => {});
        return () => { cancelled = true; };
        // Keyed on the token, not the user object. AuthProvider swaps that object once after
        // validateSession, which re-ran this, wiped the preview back to null and fired a second full
        // NAS listing for every map open — the same fix DualcamStatsPage already carries.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.token, albumId]);

    // Whichever is available: the full list once it lands, otherwise the fast preview, otherwise
    // nothing yet. The full list is always at least as complete (same indexed photo GPS plus
    // whatever video backfill found), so it fully supersedes the preview once it arrives.
    const displayMedia = media ?? previewMedia;
    // Shown while there's already something on the map but the fuller (video-inclusive) list is
    // still resolving — an unobtrusive hint that more pins may still appear, not a blocking spinner.
    const resolvingMore = loading && previewMedia !== null;
    // The item list + index handed to the player modal is scoped to whichever cluster was
    // clicked, not the whole album (see clusterByScreenProximity / marker click handler below).
    const [modalItems, setModalItems] = useState<MediaEntry[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    // Clicking a multi-item cluster opens this overview grid first (matches the Android app's
    // cluster picker) instead of dropping straight into the player at item 0 with no way to see
    // what else is there besides swiping one at a time — picking a tile opens the player modal
    // above, scoped + starting at that item. A single-item cluster skips this and opens directly.
    const [clusterOverview, setClusterOverview] = useState<LocatedEntry[] | null>(null);
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapInstanceRef = useRef<any>(null);
    const leafletRef = useRef<any>(null);
    const markersLayerRef = useRef<any>(null);
    const [mapError, setMapError] = useState<string | null>(null);
    const [mapLoadAttempt, setMapLoadAttempt] = useState(0);

    // Memoized so its identity only changes when the underlying data actually changes — otherwise
    // the rebuild-on-data-change effect below would refire on every unrelated render (e.g. opening/
    // closing the player modal), tearing down and rebuilding the markers layer for nothing.
    const located: LocatedEntry[] = useMemo(
        () => (displayMedia ?? []).filter((e): e is LocatedEntry => e.latitude != null && e.longitude != null),
        [displayMedia]
    );
    // "Latest data" ref so the map-event listeners (attached once, on map creation) always see
    // current data instead of closing over whatever `located` was at attach time.
    const locatedRef = useRef<LocatedEntry[]>(located);
    locatedRef.current = located;

    const rebuildClusters = useCallback(() => {
        const map = mapInstanceRef.current;
        const L = leafletRef.current;
        if (!map || !L) return;

        if (markersLayerRef.current) {
            markersLayerRef.current.remove();
            markersLayerRef.current = null;
        }

        const items = locatedRef.current;
        if (items.length === 0) return;

        const layer = L.layerGroup();
        clusterByScreenProximity(L, map, items, CLUSTER_RADIUS_PX).forEach(group => {
            const lat = group.reduce((sum, e) => sum + e.latitude, 0) / group.length;
            const lng = group.reduce((sum, e) => sum + e.longitude, 0) / group.length;
            const itemCount = group.length;
            let label: string;
            if (isGerman) {
                const itemLabel = itemCount === 1 ? "Element" : "Elemente";
                label = `${itemCount} ${itemLabel} an diesem Ort`;
            } else {
                const itemLabel = itemCount === 1 ? "item" : "items";
                label = `${itemCount} ${itemLabel} at this location`;
            }
            const marker = L.marker([lat, lng], {
                icon: buildClusterIcon(L, group[0], group.length),
                title: label,
                alt: "",
                keyboard: true,
            });
            marker.on("click", () => {
                if (group.length === 1) {
                    setModalItems(group);
                    setSelectedIndex(0);
                } else {
                    setClusterOverview(group);
                }
            });
            layer.addLayer(marker);
        });
        layer.addTo(map);
        markersLayerRef.current = layer;
    }, [isGerman]);

    // Creates the map + tile layer once (guarded by mapInstanceRef), fits it to the initial data,
    // then wires 'zoomend' to recluster as the user zooms (see the comment at that listener for
    // why 'moveend'/panning doesn't need it too). Runs once the "located.length > 0" branch below
    // has actually mounted the container div — keyed off `displayMedia` (preview or full list,
    // whichever is present) rather than just `media`, since the container can now mount off preview
    // data alone; mapInstanceRef's guard means a later, more complete displayMedia just lets
    // rebuildClusters (the effect below) pick up the extra pins instead of recreating the map.
    useEffect(() => {
        if (located.length === 0 || !mapContainerRef.current || mapInstanceRef.current) return;

        let cancelled = false;
        loadLeaflet()
            .then(L => {
                if (cancelled || !mapContainerRef.current) return;
                const map = L.map(mapContainerRef.current);
                mapInstanceRef.current = map;
                leafletRef.current = L;
                L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                    maxZoom: 19,
                }).addTo(map);

                const bounds = L.latLngBounds(located.map(e => [e.latitude, e.longitude]));
                if (bounds.isValid()) {
                    map.fitBounds(bounds.pad(0.15), { maxZoom: 16 });
                } else {
                    map.setView([located[0].latitude, located[0].longitude], 13);
                }

                // zoomend only — a pure pan (moveend without a zoom change) translates every
                // marker's projected screen position by the same delta, so pairwise pixel
                // distances (and therefore clusterByScreenProximity's grouping) are unchanged;
                // Leaflet already repositions lat/lng-anchored markers on pan with no help needed,
                // so reclustering there too was pure wasted rebuild work (a fresh <img> per icon).
                map.on("zoomend", rebuildClusters);
                rebuildClusters();
            })
            .catch(err => {
                setMapError((err as Error).message || (isGerman ? "Karte konnte nicht geladen werden." : "Failed to load map."));
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayMedia, mapLoadAttempt]);

    // Re-cluster when the underlying data changes (e.g. album reloaded) — the effect above only
    // (re)creates the map itself once, guarded by mapInstanceRef, so it won't pick this up.
    useEffect(() => {
        if (!mapInstanceRef.current) return;
        rebuildClusters();
    }, [located, rebuildClusters]);

    useEffect(() => () => { mapInstanceRef.current?.remove(); mapInstanceRef.current = null; }, []);

    return (
        <div className="sf-map" style={{ padding: isMobile ? "16px" : "32px 24px" }}>
            <PageHeader title="Map" extra={<Button aria-label="Back to album" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/dualcam/${albumId}`)} />} />
            {mapError && <ErrorState message={mapError} retry={() => { setMapError(null); setMapLoadAttempt(attempt => attempt + 1); }} retryLabel={isGerman ? "Erneut versuchen" : "Retry"} />}
            {!notFound && loadError && <ErrorState message={loadError.message} retry={reload} retryLabel={isGerman ? "Erneut versuchen" : "Retry"} />}

            {located.length === 0 && loading && (
                // Covers both "nothing loaded yet at all" and "the fast preview came back with zero
                // pins" — the latter can't be told apart from "really nothing has location data"
                // until the slower, video-inclusive load (still in flight here) finishes, so this
                // stays a plain loading state rather than jumping to the Empty message below.
                <LoadingState variant="grid" label="Loading locations" />
            )}
            {!(located.length === 0 && loading) && notFound && (
                <AlbumNoAccess icon={<EnvironmentOutlined style={{ fontSize: 48, color: designToken.colorTextQuaternary }} />} />
            )}
            {located.length === 0 && !loading && !notFound && !loadError && (
                <EmptyState icon={<EnvironmentOutlined />} title="Nothing in this album has location data." />
            )}
            {located.length > 0 && !notFound && (
                <div style={{ position: "relative" }}>
                    <div
                        ref={mapContainerRef}
                        style={{ height: "70vh", borderRadius: designToken.borderRadius, overflow: "hidden" }}
                    />
                    {resolvingMore && (
                        <div style={{ position: "absolute", top: 12, right: 12, zIndex: 500 }}>
                            <StatusTag tone="info"><Spin size="small" style={{ marginRight: 6 }} /> Resolving more locations…</StatusTag>
                        </div>
                    )}
                </div>
            )}

            <Modal
                open={clusterOverview !== null}
                onCancel={() => setClusterOverview(null)}
                footer={null}
                title={clusterOverview ? `${clusterOverview.length} items at this location` : undefined}
                width="min(720px, 96vw)"
                destroyOnHidden
            >
                <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(120px, 1fr))`, gap: 10 }}>
                    {clusterOverview?.map((entry, idx) => (
                        <MediaThumbTile
                            key={`${entry.id}-${idx}`}
                            entry={entry}
                            borderRadius={designToken.borderRadius}
                            onClick={() => {
                                setModalItems(clusterOverview);
                                setSelectedIndex(idx);
                                setClusterOverview(null);
                            }}
                        />
                    ))}
                </div>
            </Modal>

            <DualcamPlayerModal items={modalItems} index={selectedIndex} onIndexChange={setSelectedIndex} />
        </div>
    );
};

export default DualcamMapPage;
